import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, X, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  COOKIE_BANNER_OPEN_EVENT,
  CONSENT_EVENT,
  DEFAULT_CONSENT,
  getConsent,
  setConsent,
} from "@/lib/cookieConsent";

const CATEGORIES = [
  {
    key: "essential",
    label: "Essential",
    description:
      "Required for the site to function correctly — admin session, lead form submission, secure navigation. Always on.",
    Icon: Shield,
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Helps us understand how visitors interact with the site (PostHog, Google Analytics) so we can improve content and performance.",
    Icon: BarChart3,
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Allows us to deliver personalised property recommendations and remarketing campaigns on third-party platforms.",
    Icon: Megaphone,
  },
];

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CONSENT);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // On mount: show if no consent yet (but never on admin routes)
  useEffect(() => {
    if (isAdminRoute) {
      setVisible(false);
      return undefined;
    }
    const existing = getConsent();
    if (!existing) {
      // Defer briefly so it doesn't compete with hero animations
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setChoices(existing);
    return undefined;
  }, [isAdminRoute]);

  // Allow re-opening from Footer "Cookie Preferences" link
  useEffect(() => {
    const onOpen = () => {
      if (isAdminRoute) return; // ignore on admin pages
      const existing = getConsent() || DEFAULT_CONSENT;
      setChoices(existing);
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
  }, [isAdminRoute]);

  const persist = (consent) => {
    setConsent(consent);
    setVisible(false);
    setCustomizing(false);
  };

  const onAcceptAll = () => persist({ essential: true, analytics: true, marketing: true });
  const onRejectAll = () => persist({ essential: true, analytics: false, marketing: false });
  const onSaveCustom = () => persist({ ...choices, essential: true });

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="cookie-consent-banner"
          className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-6 pointer-events-none"
          role="dialog"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
        >
          <div
            className={cn(
              "pointer-events-auto mx-auto w-full max-w-5xl",
              "rounded-2xl border border-white/10 bg-[rgba(10,10,10,0.92)] backdrop-blur-2xl",
              "shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_1px_rgba(212,175,55,0.18)_inset]",
              "overflow-hidden"
            )}
          >
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[color:rgba(212,175,55,0.10)] blur-3xl pointer-events-none" />

            <div className="relative p-5 sm:p-6 lg:p-7">
              {/* Header */}
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.4)] bg-[color:var(--sp-gold-soft)]">
                  <Cookie className="h-5 w-5 text-[color:var(--sp-gold)]" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                    <span className="gold-divider" /> Cookie & Privacy Preferences
                  </p>
                  <h2
                    id="cookie-consent-title"
                    className="font-display text-xl sm:text-2xl mt-2 text-white leading-tight"
                  >
                    Your privacy, your choice.
                  </h2>
                  <p
                    id="cookie-consent-desc"
                    className="mt-2 text-sm text-white/70 leading-relaxed max-w-3xl"
                  >
                    We use cookies to improve your browsing experience, analyse site
                    traffic, and deliver personalised content. Essential cookies are
                    required for the site to function; you can accept, reject, or
                    customise the rest. Read our{" "}
                    <Link
                      to="/privacy"
                      className="text-[color:var(--sp-gold)] hover:underline whitespace-nowrap"
                      data-testid="cookie-consent-policy-link"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close (reject non-essential cookies)"
                  onClick={onRejectAll}
                  data-testid="cookie-consent-close"
                  className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/55 hover:text-white hover:border-white/30 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Customise panel */}
              <AnimatePresence initial={false}>
                {customizing && (
                  <motion.div
                    key="customise"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-6 grid gap-3">
                      {CATEGORIES.map(({ key, label, description, Icon, locked }) => (
                        <div
                          key={key}
                          data-testid={`cookie-category-${key}`}
                          className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.025] p-4"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 bg-black/30">
                            <Icon className="h-4 w-4 text-[color:var(--sp-gold)]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-sm font-medium text-white">{label}</p>
                              <Switch
                                data-testid={`cookie-toggle-${key}`}
                                checked={locked ? true : Boolean(choices[key])}
                                disabled={locked}
                                onCheckedChange={(v) =>
                                  setChoices((p) => ({ ...p, [key]: v }))
                                }
                                aria-label={`Toggle ${label} cookies`}
                              />
                            </div>
                            <p className="mt-1 text-xs text-white/60 leading-relaxed">
                              {description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                {customizing ? (
                  <button
                    type="button"
                    onClick={() => setCustomizing(false)}
                    data-testid="cookie-consent-back"
                    className="text-xs tracking-[0.22em] uppercase text-white/55 hover:text-white transition-colors self-start sm:self-auto"
                  >
                    ← Back
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCustomizing(true)}
                    data-testid="cookie-consent-customize"
                    className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-white/70 hover:text-[color:var(--sp-gold)] transition-colors self-start sm:self-auto"
                  >
                    Customise <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    type="button"
                    onClick={onRejectAll}
                    data-testid="cookie-consent-reject"
                    className="btn-ghost inline-flex items-center justify-center px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap"
                  >
                    Reject All
                  </button>
                  {customizing && (
                    <button
                      type="button"
                      onClick={onSaveCustom}
                      data-testid="cookie-consent-save"
                      className="btn-ghost inline-flex items-center justify-center px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap border-[color:rgba(212,175,55,0.4)] text-[color:var(--sp-gold)] hover:bg-[color:var(--sp-gold-soft)]"
                    >
                      Save Preferences
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onAcceptAll}
                    data-testid="cookie-consent-accept-all"
                    className="btn-gold inline-flex items-center justify-center px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;

// Re-export the consent event for external listeners (analytics loaders, etc.)
export { CONSENT_EVENT };
